import { useEffect, useRef } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { CenteredSpinner } from '@components/ui/feedback';
import { useJotBillAccess } from '@features/billing/hooks/useJotBillAccess';
import { StartGroupSection } from '@features/organizations/components/StartGroupSection';
import {
  useGetOrganizationMembersQuery,
  useOrganizationAccess,
  useTransformMembers,
} from '@features/organizations/store';
import { useOrgReviewCount } from '@features/practice-data-import';
import { useCan, useFeatureFlag, useOrganizationInfo } from '@features/user';
import { Box, Card, CardContent, Container, Tab, Tabs, Typography, useTheme } from '@mui/material';
import { FeatureGates, useFeatureGate } from '@services/statsig';

import { useStickyHeaderDetection } from '../../hooks/useStickyHeaderDetection';

/**
 * Layout component for /practice/* routes
 * Handles authentication, permissions, and provides shared header with tabs
 */
export const PracticeLayout = () => {
  const theme = useTheme();
  const { userIsInGroup, userIsBillingAdmin, orgLoading } = useOrganizationAccess();
  const location = useLocation();
  const navigate = useNavigate();
  // ADR 0073 Phase 3 (GDN-1590): parity with hasBillingAccess.
  const canManageBilling = useCan('manage_billing');
  const canManageOrganization = useCan('manage_organization');
  const canManageAppointmentTypes = useCan('manage_appointment_types');
  const canManageAppointmentLabels = useCan('manage_appointment_labels');
  // GDN-2187: self-serve scheduling settings (availability + booking gates).
  // Gated on the same calendar-scope permission the backend requires for gate
  // writes (manage_org_calendar), AND behind a feature gate so the surface stays
  // hidden until rollout (default-off).
  const canManageScheduling = useCan('manage_org_calendar');
  const schedulingFeatureEnabled = useFeatureGate(FeatureGates.PATIENT_SELF_SCHEDULING);
  // GDN-2482: the consolidated "Schedule & Documentation" tab replaces the
  // separate Scheduling + Appointment Types + Appointment Labels tabs and adds
  // a Note Templates section (double-gated: catalog gate + manage_templates,
  // matching TemplateCatalogPage's own check). Scheduling keeps its GDN-2187
  // gating; labels follow their manage_appointment_labels permission. The tab
  // shows whenever any of its sub-sections would.
  const canManageTemplates = useCan('manage_templates');
  const templateCatalogEnabled = useFeatureGate(
    FeatureGates.PRACTICE_TEMPLATE_CATALOG,
  );
  const showScheduleAndDocumentation =
    (templateCatalogEnabled && canManageTemplates) ||
    canManageAppointmentTypes ||
    canManageAppointmentLabels ||
    (canManageScheduling && schedulingFeatureEnabled);
  // ADR 0073 Phase 2b (GDN-1589): subscription surfaces gate on
  // manage_subscription (practice_administrator only), not manage_billing.
  const canManageSubscription = useCan('manage_subscription');
  // GDN-2271: practice-admin "Data import" tab — shown only when a staff admin
  // has escalated import resources that need this practice's decision. The hook
  // skips its query for non-administrators, so the count stays 0 for them.
  // (Will also host practice-admin file upload in a later increment.)
  const orgReviewCount = useOrgReviewCount();
  const { hasAccess: hasBillingFeature } = useJotBillAccess();
  const stripeConnectFeatureFlag = useFeatureFlag('stripe_connect');
  // Solo orgs (single-clinician practices) collapse the Members surface into the
  // Solo subscription page — hide the Members tab. Reappears automatically
  // when `group` flips to true post-invite-accept (hard reload pattern).
  const isGroupPractice = useOrganizationInfo()?.group === true;

  // Active-members count for the "Members (N)" tab label. RTK Query dedupes
  // against `CliniciansPage`'s identical call, so this is cache-warm whenever
  // the user lands on the Members tab.
  const { data: membersData } = useGetOrganizationMembersQuery({}, { skip: !isGroupPractice });
  const { activeMembers } = useTransformMembers(membersData?.members);
  const activeMembersCount = activeMembers.length;

  // Sticky header detection
  const { registerSentinel, registerHeader, isStuck } = useStickyHeaderDetection();
  const sentinelRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const headerId = 'sticky-header-practice-layout';

  // Register sentinel after the component renders the actual content (not early returns)
  // Dependencies include states that determine early returns, so effect re-runs when content renders
  useEffect(() => {
    const sentinel = sentinelRef.current;
    const header = headerRef.current;

    if (sentinel) {
      registerSentinel(headerId, sentinel);
    }
    if (header) {
      registerHeader(headerId, header);
    }

    return () => {
      registerSentinel(headerId, null);
      registerHeader(headerId, null);
    };
  }, [
    userIsInGroup,
    orgLoading,
    userIsBillingAdmin,
    canManageOrganization,
    canManageAppointmentTypes,
    canManageAppointmentLabels,
    registerSentinel,
    registerHeader,
  ]);

  const headerIsStuck = isStuck(headerId);

  // User is not part of any organization
  if (!userIsInGroup) {
    return (
      <Box sx={{ p: 3, maxWidth: 1200, margin: 'auto', minHeight: '100%' }}>
        <StartGroupSection />
      </Box>
    );
  }

  // Loading organization data
  if (orgLoading) {
    return <CenteredSpinner message="Loading organization data..." />;
  }

  const canAccessPracticeManagement =
    userIsBillingAdmin ||
    canManageOrganization ||
    canManageAppointmentTypes ||
    canManageAppointmentLabels ||
    // GDN-2187: a scheduling-only coordinator may hold manage_org_calendar
    // alone (ADR-0073 decouples these grants). Without this, they'd see the
    // Scheduling tab/nav entry but hit the layout's access-denied early-return.
    // Feature-flag-guarded so the surface stays hidden until rollout.
    (canManageScheduling && schedulingFeatureEnabled) ||
    // GDN-2482: manage_templates is org-configurable, so a delegated
    // template manager may hold it alone. Same contract as scheduling: every
    // term of showScheduleAndDocumentation must also grant layout access, or
    // the tab/nav entry would promise a surface the layout then denies.
    (templateCatalogEnabled && canManageTemplates);

  // User doesn't have practice management access
  if (!canAccessPracticeManagement) {
    return (
      <Box sx={{ p: 3, maxWidth: 1200, margin: 'auto' }}>
        <Typography variant="h4" gutterBottom>
          Your Practice
        </Typography>
        <Card>
          <CardContent>
            <Typography>
              You don&apos;t have permission to manage this organization. Contact your practice
              administrator for access.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Determine current tab based on pathname
  // Sub-routes like billing-data-migration should highlight their parent tab
  // Manage views that survive as routes but no longer have their own tab —
  // highlight the consolidated Schedule & Documentation tab there. (The old
  // appointment-labels path redirects outright — its manager is embedded in
  // the section.)
  const scheduleDocumentationManageViews = ['/practice/scheduling-settings'];
  const currentTab = location.pathname.startsWith('/practice/billing-data-migration')
    ? '/practice/billing-settings'
    : scheduleDocumentationManageViews.includes(location.pathname)
      ? '/practice/schedule-documentation'
      : location.pathname;

  const handleTabChange = (_: React.SyntheticEvent, newValue: string) => {
    navigate(newValue);
  };

  return (
    <Box sx={{ position: 'relative', minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Sentinel element for sticky detection */}
      <div
        ref={sentinelRef}
        data-header-id={headerId}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 1,
          pointerEvents: 'none',
          zIndex: 1,
        }}
      />

      {/* Sticky Header with Tabs */}
      <Box
        ref={headerRef}
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: theme.zIndex.appBar - 1,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Container maxWidth={'lg'}>
          <Box
            sx={{
              pt: 3,
              pb: 0,
            }}
          >
            {/* Title and Subtitle */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="h4" sx={{ fontWeight: 500 }}>
                Your Practice
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Manage your organization
              </Typography>
            </Box>

            {/* Tabs Navigation */}
            <Tabs
              value={currentTab}
              onChange={handleTabChange}
              textColor="primary"
              indicatorColor="primary"
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                '& .MuiTab-root': {
                  textTransform: 'none',
                  minHeight: 48,
                  fontSize: '0.9375rem',
                },
                boxShadow: headerIsStuck ? '0 2px 4px 0 rgba(0, 0, 0, 0.08)' : 'none',
                transition: headerIsStuck ? 'box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1)' : 'none',
                clipPath: headerIsStuck ? 'inset(0px 0 -10px 0)' : 'inset(0 0 0 0)',
              }}
            >
              {/* Members first for group orgs — it's the primary configuration
                  surface (where plan changes happen) and the default landing
                  destination for groups. Hidden for solo orgs; shows the active
                  count. */}
              {isGroupPractice && (
                <Tab
                  label={activeMembersCount > 0 ? `Members (${activeMembersCount})` : 'Members'}
                  value="/practice/clinicians"
                />
              )}
              {/* Subscription gated on the manage_subscription permission
                  (ADR 0073 — practice_administrator). For solo orgs this is the
                  only practice tab. */}
              {canManageSubscription && (
                <Tab label="Subscription" value="/practice/subscriptions" />
              )}
              {/* GDN-2271: only for practice admins (manage_organization), and
                  only while there are items pending — when the count is 0 the
                  practice UI is unchanged. Mirrors the Members count-in-label
                  pattern. */}
              {canManageOrganization && orgReviewCount > 0 && (
                <Tab label={`Data import (${orgReviewCount})`} value="/practice/data-import" />
              )}
              {hasBillingFeature && canManageBilling && (
                <Tab label="Billing & Claims" value="/practice/billing-settings" />
              )}
              {/* <Tab label="Payer Docs Center" value="/practice/payer-docs-center" /> */}
              {showScheduleAndDocumentation && (
                <Tab
                  label="Schedule & Documentation"
                  value="/practice/schedule-documentation"
                />
              )}
              {stripeConnectFeatureFlag && (
                <Tab label="Patient Payments" value="/practice/payments" />
              )}
              {canManageOrganization && (
                <Tab label="Practice Settings" value="/practice/settings" />
              )}
            </Tabs>
          </Box>
        </Container>
      </Box>

      {/* Main Content */}
      <Container
        sx={{ py: 4, flexGrow: 1, display: 'flex', flexDirection: 'column' }}
        maxWidth={'lg'}
      >
        <Outlet />
      </Container>
    </Box>
  );
};
